import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../utils/api";
import "./OrganizationDetails.css";
import { useAuth } from '../../context/AuthContext'
import { MANAGEABLE_TEMPLATE_OPTIONS } from '../Dashboard/PageBuilder/Registry/index.js'
function OrganizationDetails() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSeasons, setShowSeasons] = useState(true);
  const [showUsers, setShowUsers] = useState(true);
  const [showOrgInfo, setShowOrgInfo] = useState(true);
  const [showFeatures, setShowFeatures] = useState(true);
  const [showWebsite, setShowWebsite] = useState(true);
  const [showBranding, setShowBranding] = useState(true);
  const [plan, setPlan] = useState("free");
  const [systemPagesMessage, setSystemPagesMessage] = useState("");
  const [planMessage, setPlanMessage] = useState("");
  const [orgPages, setOrgPages] = useState([]);
  const PLAN_OPTIONS = [
    { value: "free", label: "Free Listing" },
    { value: "trial", label: "Trial" },
    { value: "listing_plus", label: "Listing Plus" },
    { value: "league_tools", label: "League Tools" },
    { value: "branded_site", label: "Branded Site" },
    { value: "custom_domain", label: "Custom Domain" },
  ];
  // user modals
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "staff",
  });
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // season modals
  const [isCreateSeasonModalOpen, setIsCreateSeasonModalOpen] = useState(false);
  const [isEditSeasonModalOpen, setIsEditSeasonModalOpen] = useState(false);
  const [seasonForm, setSeasonForm] = useState({
    name: "",
    startDate: "",
    finishDate: "",
    active: true,
    showDivision: "false"
  });
  const [editingSeason, setEditingSeason] = useState(null);
  const [features, setFeatures] = useState([]);
  const [templateAccess, setTemplateAccess] = useState([]);
  const requiredSystemKeys = [
    "teams",
    "players",
    "schedule",
    "games",
    "standings",
    "leaderboard",
  ];

  const existingSystemKeys = new Set(
    orgPages
      .filter((page) => page.page_type === "system")
      .map((page) => page.system_key)
  );

  const missingSystemKeys = requiredSystemKeys.filter(
    (key) => !existingSystemKeys.has(key)
  );

  const hasAllSystemPages = missingSystemKeys.length === 0;

  const navigate = useNavigate()
  const { setOrganization, user } = useAuth();

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        const res = await api.get(`/api/organizations/${id}`, {
          withCredentials: true,
        });
        const pagesRes = await api.get(`/api/organization-page/${id}`, {
          withCredentials: true,
        });
        setOrgPages(pagesRes.data?.pages || []);
        setOrg(res.data.organization);
        setUsers(res.data.organization.users || []);
        setOrganization(res.data.organization);
        setPlan(res.data.organization?.plan || "free");
        setTemplateAccess(res.data.organization?.templateAccess || []);
        // fetch seasons via nested route
        const seasonRes = await api.get(`/api/organizations/${id}/seasons`, {
          withCredentials: true,
        });
        setSeasons(seasonRes.data.seasons || []);
        const featuresRes = await api.get(`/api/organization-features/${id}`, {
          withCredentials: true,
        });

        setFeatures(featuresRes.data.features || []);

      } catch (err) {
        console.error("Error fetching org details", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrgData();
  }, [id]);

  const handleInitializeSystemPages = async () => {
    try {
      const res = await api.post(
        `/api/organizations/${id}/system-pages/init`,
        {},
        { withCredentials: true }
      );

      const pagesRes = await api.get(`/api/organization-page/${id}`, {
        withCredentials: true,
      });
      setOrgPages(pagesRes.data?.pages || []);

      setSystemPagesMessage(res.data?.message || "System pages initialized.");
      setTimeout(() => setSystemPagesMessage(""), 3000);
    } catch (err) {
      console.error("Failed to initialize system pages", err);
      setSystemPagesMessage(
        err.response?.data?.message || "Failed to initialize system pages."
      );
      setTimeout(() => setSystemPagesMessage(""), 3000);
    }
  };

  const handleToggleTemplateAccess = async (templateId, enabled) => {
    try {
      if (enabled) {
        await api.post(
          `/api/organizations/${id}/template-access`,
          { template_id: templateId },
          { withCredentials: true }
        );

        setTemplateAccess((prev) => [
          ...prev,
          { organization_id: Number(id), template_id: templateId },
        ]);
      } else {
        await api.delete(`/api/organizations/${id}/template-access/${templateId}`, {
          withCredentials: true,
        });

        setTemplateAccess((prev) =>
          prev.filter((item) => item.template_id !== templateId)
        );
      }
    } catch (err) {
      console.error("Failed to update template access", err);
      alert("Failed to update template access.");
    }
  };
  // ========== USER HANDLERS ==========
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...userForm,
        organization_id: id,
      };
      await api.post("/api/usersv2", body, { withCredentials: true });
      const res = await api.get(`/api/organizations/${id}`, {
        withCredentials: true,
      });
      setUsers(res.data.organization.users || []);
      setIsCreateUserModalOpen(false);
    } catch (err) {
      console.error("Error creating user", err);
      alert(err.response?.data?.message || "Error creating user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const body = {
        username: userForm.username,
        email: userForm.email.trim(),
        role: userForm.role,
        resetPassword: userForm.resetPassword || false,
      };

      const res = await api.put(`/api/usersv2/${editingUser.id}`, body, {
        withCredentials: true,
      });

      if (res.data.tempPassword) {
        alert(`Temporary Password: ${res.data.tempPassword}`);
      }

      const updated = await api.get(`/api/organizations/${id}`, {
        withCredentials: true,
      });
      setUsers(updated.data.organization.users || []);

      setIsEditUserModalOpen(false);
    } catch (err) {
      console.error("Error updating user", err);
      alert(err.response?.data?.message || "Error updating user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await api.delete(`/api/usersv2/${userId}`, { withCredentials: true });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      console.error("Error deleting user", err);
      alert(err.response?.data?.message || "Error deleting user");
    }
  };
  const handleResetTrial = async () => {
    const confirmed = window.confirm(
      "Allow this organization to start a trial again? This will clear trial dates, set has_used_trial to false, and move the plan to Free."
    );

    if (!confirmed) return;

    try {
      const res = await api.post(
        `/api/organizations/${id}/reset-trial`,
        {},
        { withCredentials: true }
      );

      const updated = await api.get(`/api/organizations/${id}`, {
        withCredentials: true,
      });

      setOrg(updated.data.organization);
      setPlan(updated.data.organization?.plan || "free");
      setPlanMessage(
        res.data?.message || "Trial reset. Organization can start a trial again."
      );
      setTimeout(() => setPlanMessage(""), 2500);
    } catch (err) {
      console.error("Failed to reset trial", err);
      setPlanMessage(err.response?.data?.message || "Failed to reset trial.");
      setTimeout(() => setPlanMessage(""), 2500);
    }
  };
  // ========== SEASON HANDLERS ==========
  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/organizations/${id}/seasons`, seasonForm, {
        withCredentials: true,
      });
      const res = await api.get(`/api/organizations/${id}/seasons`, {
        withCredentials: true,
      });
      setSeasons(res.data.seasons || []);
      setIsCreateSeasonModalOpen(false);
      setSeasonForm({ name: "", startDate: "", finishDate: "", active: true });
    } catch (err) {
      console.error("Error creating season", err);
    }
  };

  // handle delete
  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm("Are you sure you want to delete this season?")) return;
    try {
      await api.delete(`/api/seasonsv2/${seasonId}`, { withCredentials: true });
      setSeasons((prev) => prev.filter((s) => s.id !== seasonId));
    } catch (err) {
      console.error("Error deleting season:", err);
      alert(err.response?.data?.message || "Error deleting season");
    }
  };

  // handle update
  const handleUpdateSeason = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put(`/api/seasonsv2/${editingSeason.id}`, editingSeason, { withCredentials: true });
      // refresh seasons after update
      const seasonRes = await api.get(`/api/organizations/${id}/seasons`, { withCredentials: true });
      setSeasons(seasonRes.data.seasons || []);
      setIsEditSeasonModalOpen(false);
    } catch (err) {
      console.error("Error updating season:", err);
      alert(err.response?.data?.message || "Error updating season");
    }
  };
  const enabledTemplateIds = new Set(
    templateAccess.map((item) => item.template_id)
  );
  const now = new Date();

  const trialStartedAt = org?.trial_started_at ? new Date(org.trial_started_at) : null;
  const trialEndsAt = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;

  const isTrialActive =
    org?.plan === "trial" &&
    trialEndsAt &&
    trialEndsAt > now;

  const isTrialExpired =
    !!trialEndsAt && trialEndsAt <= now;

  const trialStatus = !org?.has_used_trial
    ? "Not started"
    : isTrialActive
      ? "Active"
      : isTrialExpired
        ? "Expired"
        : org?.plan === "trial"
          ? "Active"
          : "Used";
  // ========== RENDER ==========
  if (loading) return <p>Loading...</p>;
  if (!org) return <p>Organization not found</p>;
  const orgSport = org.sports?.map((sport) => sport.type).join(", ");

  return (
    <div className="organization-details-container">
      <div className="organization-info-container">
        <div
          className="organization-info-header"
          onClick={() => setShowOrgInfo((prev) => !prev)}
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <h3>
            Organization Info <span>{showOrgInfo ? "▲" : "▼"}</span>
          </h3>
        </div>
        {showOrgInfo && (
          <table className="org-info-table">
            <tbody>
              <tr>
                <th>Name</th>
                <td>{org.name}</td>
              </tr>
              <tr>
                <th>ID</th>
                <td>{org.id}</td>
              </tr>
              <tr>
                <th>Domain</th>
                <td>{org.domain || "None"}</td>
              </tr>
              <tr>
                <th>Slug</th>
                <td>{org.slug}</td>
              </tr>
              <tr>
                <th>Sports</th>
                <td>{org.sports?.map((sport) => sport.type).join(", ") || "None"}</td>
              </tr>
              <tr>
                <th>Plan</th>
                <td>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    disabled={user?.role !== "super_admin"}
                  >
                    {PLAN_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {user?.role === "super_admin" && (
                    <button
                      style={{ marginLeft: 8 }}
                      onClick={async () => {
                        try {
                          await api.put(
                            `/api/organizations/${id}/plan`,
                            { plan },
                            { withCredentials: true }
                          );

                          const updated = await api.get(`/api/organizations/${id}`, {
                            withCredentials: true,
                          });
                          setOrg(updated.data.organization);

                          setPlanMessage("Plan updated successfully.");
                          setTimeout(() => setPlanMessage(""), 2500);
                        } catch (err) {
                          setPlanMessage("Failed to update plan.");
                          setTimeout(() => setPlanMessage(""), 2500);
                        }
                      }}
                    >
                      Save
                    </button>
                  )}
                </td>
              </tr>
              <tr>
                <th>Trial</th>
                <td>
                  Status: {trialStatus}
                  <br />
                  Started: {org?.trial_started_at ? new Date(org.trial_started_at).toLocaleString() : "—"}
                  <br />
                  Ends: {org?.trial_ends_at ? new Date(org.trial_ends_at).toLocaleString() : "—"}
                  <br />
                  Used: {org?.has_used_trial ? "Yes" : "No"}
                  <br />
                  <button
                    onClick={handleResetTrial}
                  >
                    Allow Trial Again
                  </button></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {planMessage && (
        <div style={{ marginTop: 8, color: "green", fontSize: 14 }}>
          {planMessage}
        </div>
      )}
      <div className="organization-users-container">
        <div
          className="organization-users-container-header"
          onClick={() => setShowUsers(prev => !prev)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <h3>
            Users <span>{showUsers ? "▲" : "▼"}</span>
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation(); // prevent toggling when clicking Add User
              setIsCreateUserModalOpen(true);
            }}
          >
            + Add User
          </button>
        </div>

        {showUsers && (
          <table className="user-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6">No users yet.</td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>{u.role}</td>
                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</td>
                    <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}</td>
                    <td>
                      <button
                        onClick={() => {
                          setEditingUser(u);
                          setUserForm({
                            username: u.username,
                            email: u.email || "",
                            role: u.role || "staff",
                            resetPassword: false,
                          });
                          setIsEditUserModalOpen(true);
                        }}
                      >
                        ✏️
                      </button>
                      <button onClick={() => handleDeleteUser(u.id)}>🗑️</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="organization-features-container">
        <div
          className="organization-features-header"
          onClick={() => setShowFeatures(prev => !prev)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <h3>
            Features <span>{showFeatures ? "▲" : "▼"}</span>
          </h3>
        </div>

        {showFeatures && (
          <div className="features-list">
            {features.map((feature) => (
              <label key={feature.id} style={{ display: "block", marginBottom: "5px" }}>
                <input
                  type="checkbox"
                  checked={feature.enabled}
                  onChange={async (e) => {
                    const newValue = e.target.checked;

                    // Optimistically update UI
                    setFeatures((prev) =>
                      prev.map((f) =>
                        f.id === feature.id ? { ...f, enabled: newValue } : f
                      )
                    );

                    try {
                      await api.put(
                        `/api/organization-features/${feature.id}`,
                        { enabled: newValue },
                        { withCredentials: true }
                      );
                    } catch (err) {
                      console.error("Error updating feature", err);
                      alert("Failed to update feature");

                      // Rollback
                      setFeatures((prev) =>
                        prev.map((f) =>
                          f.id === feature.id ? { ...f, enabled: !newValue } : f
                        )
                      );
                    }
                  }}
                />
                {feature.feature_key}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="organization-branding-container">
        <div
          className="organization-branding-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3>Sport Settings</h3>

          <div onClick={(e) => e.stopPropagation()}>
            <button onClick={() => navigate(`/dashboard/admin/organizations/${id}/stats`)}>
              Manage Stats →
            </button>

            {/* <button onClick={() => navigate(`/dashboard/admin/organizations/${id}/game-periods`)}>
              Manage Game Periods →
            </button>

            <button onClick={() => navigate(`/dashboard/admin/organizations/${id}/player-attributes`)}>
              Manage Player Attributes →
            </button> */}
          </div>
        </div>
      </div>




      {/* USERS MODALS */}
      {isCreateUserModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>Create User</h3>
            <form onSubmit={handleCreateUser}>
              <input
                type="text"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                placeholder="Username"
                required
              />
              <input
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                placeholder="Email"
              />
              <input
                type="password"
                value={userForm.password}
                onChange={(e) =>
                  setUserForm({ ...userForm, password: e.target.value })
                }
                placeholder="Password"
              />
              <select
                value={userForm.role}
                onChange={(e) =>
                  setUserForm({ ...userForm, role: e.target.value })
                }
              >
                <option value="org_owner">Organization Owner</option>
                <option value="staff">Staff</option>
              </select>
              <button type="submit">Create</button>
              <button
                type="button"
                onClick={() => setIsCreateUserModalOpen(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      {isEditUserModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit User</h3>
            <form onSubmit={handleUpdateUser}>
              <input
                type="text"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                required
              />
              <input
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
              />
              <select
                value={userForm.role}
                onChange={(e) =>
                  setUserForm({ ...userForm, role: e.target.value })
                }
              >
                <option value="org_owner">Organization Owner</option>
                <option value="staff">Staff</option>
              </select>
              <label>
                <input
                  type="checkbox"
                  checked={userForm.resetPassword || false}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      resetPassword: e.target.checked,
                    })
                  }
                />
                Reset Password
              </label>
              <button type="submit">Update</button>
              <button
                type="button"
                onClick={() => setIsEditUserModalOpen(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
      <div className="organization-season-container">
        <div className="organization-season-container-header"
          onClick={() => setShowSeasons(prev => !prev)}
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Seasons     <span>{showSeasons ? "▲" : "▼"}</span></h3>

          <button onClick={() => setIsCreateSeasonModalOpen(true)}>
            + Add Season
          </button>
        </div>

        {showSeasons && (

          <table className="season-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Start</th>
                <th>Finish</th>
                <th>Active</th>
                <th>Tournament</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {seasons.length === 0 ? (
                <tr>
                  <td colSpan="5">No seasons yet</td>
                </tr>
              ) : (
                seasons.map((s) => (
                  <tr key={s.id}>
                    <td
                      className="season-name-link"
                      onClick={() => navigate(`/dashboard/seasons/${s.id}`)}
                    >
                      {s.name}
                    </td>
                    <td>{s.startDate?.split("T")[0]}</td>
                    <td>{s.finishDate?.split("T")[0]}</td>
                    <td>{s.active ? "✅" : "❌"}</td>
                    <td>{s.showDivisions ? "✅" : "❌"}</td>

                    <td>
                      <button
                        className="organization-season-table-button"
                        onClick={() => {
                          setEditingSeason({
                            ...s,
                            startDate: s.startDate?.split("T")[0],
                            finishDate: s.finishDate?.split("T")[0],
                          });
                          setIsEditSeasonModalOpen(true);
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="organization-season-table-button"
                        onClick={() => handleDeleteSeason(s.id)}>🗑️</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

      </div>
      {/* WEBSITE SECTION */}
      <div className="organization-website-container">
        <div
          className="organization-website-header"
          onClick={() => setShowWebsite((prev) => !prev)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3>
            Website <span>{showWebsite ? "▲" : "▼"}</span>
          </h3>

          <div
            className="organization-website-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => navigate(`/dashboard/organizations/${id}`)}>
              Manage Website →
            </button>

            <button
              onClick={() =>
                navigate(`/dashboard/organizations/${id}/website-settings`)
              }
            >
              Website Settings →
            </button>

            {(org.domain || org.slug) && (
              <button
                onClick={() => {
                  const publicUrl = org.domain
                    ? `https://${org.domain}`
                    : `/${org.slug}`;
                  window.open(publicUrl, "_blank");
                }}
              >
                View Public Site ↗
              </button>
            )}
          </div>
        </div>

        {showWebsite && (
          <table className="org-info-table">
            <tbody>
              <tr>
                <th>Slug URL</th>
                <td>{org.slug ? `sportinghip.com/${org.slug}` : "None"}</td>
              </tr>
              <tr>
                <th>Custom Domain</th>
                <td>{org.domain || "None"}</td>
              </tr>
              <tr>
                <th>Template Access</th>
                <td>
                  {MANAGEABLE_TEMPLATE_OPTIONS.length === 0 ? (
                    "No special templates."
                  ) : (
                    <div className="template-access-list">
                      {MANAGEABLE_TEMPLATE_OPTIONS.map((template) => (
                        <label key={template.id} className="template-access-item">
                          <input
                            type="checkbox"
                            checked={enabledTemplateIds.has(template.id)}
                            onChange={(e) =>
                              handleToggleTemplateAccess(template.id, e.target.checked)
                            }
                          />
                          <span className="template-access-label">{template.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
              {/* <tr>
                <th>System Pages</th>
                <td>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                    <span>
                      {hasAllSystemPages
                        ? "Active"
                        : `Missing: ${missingSystemKeys.join(", ")}`}
                    </span>

                    {!hasAllSystemPages && (
                      <button onClick={handleInitializeSystemPages}>
                        Create Missing System Pages
                      </button>
                    )}

                    {systemPagesMessage && (
                      <span style={{ fontSize: "14px", color: "green" }}>
                        {systemPagesMessage}
                      </span>
                    )}
                  </div>
                </td>
              </tr> */}
            </tbody>
          </table>
        )}
      </div>
      {/* BRANDING SECTION */}
      <div className="organization-branding-container">
        <div
          className="organization-branding-header"
          onClick={() => setShowBranding((prev) => !prev)}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3>
            Branding <span>{showBranding ? "▲" : "▼"}</span>
          </h3>

          <div onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => navigate(`/dashboard/admin/organizations/${id}/branding`)}
            >
              Manage Branding →
            </button>
          </div>
        </div>

        {showBranding && (
          <table className="org-info-table">
            <tbody>
              <tr>
                <th>Branding</th>
                <td>Manage logos, colors, and organization brand assets.</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>



      {/* SEASONS MODALS */}
      {isCreateSeasonModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>Create Season</h3>
            <form onSubmit={handleCreateSeason}>
              <input
                type="text"
                value={seasonForm.name}
                onChange={(e) =>
                  setSeasonForm({ ...seasonForm, name: e.target.value })
                }
                placeholder="Season Name"
                required
              />
              <input
                type="date"
                value={seasonForm.startDate}
                onChange={(e) =>
                  setSeasonForm({ ...seasonForm, startDate: e.target.value })
                }
              />
              <input
                type="date"
                value={seasonForm.finishDate}
                onChange={(e) =>
                  setSeasonForm({ ...seasonForm, finishDate: e.target.value })
                }
              />
              <label>
                <input
                  type="checkbox"
                  checked={seasonForm.active}
                  onChange={(e) =>
                    setSeasonForm({
                      ...seasonForm,
                      active: e.target.checked,
                    })
                  }
                />
                Active
              </label>
              <button type="submit">Create</button>
              <button
                type="button"
                onClick={() => setIsCreateSeasonModalOpen(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      {isEditSeasonModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Season</h3>
            <form onSubmit={handleUpdateSeason}>
              <input
                type="text"
                value={editingSeason.name}
                onChange={(e) =>
                  setEditingSeason({ ...editingSeason, name: e.target.value })
                }
              />
              <input
                type="date"
                value={editingSeason.startDate?.split("T")[0] || ""}
                onChange={(e) =>
                  setEditingSeason({
                    ...editingSeason,
                    startDate: e.target.value,
                  })
                }
              />
              <input
                type="date"
                value={editingSeason.finishDate?.split("T")[0] || ""}
                onChange={(e) =>
                  setEditingSeason({
                    ...editingSeason,
                    finishDate: e.target.value,
                  })
                }
              />
              <label>
                <input
                  type="checkbox"
                  checked={editingSeason.active}
                  onChange={(e) =>
                    setEditingSeason({
                      ...editingSeason,
                      active: e.target.checked,
                    })
                  }
                />
                Active
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={editingSeason.showDivisions}
                  onChange={(e) =>
                    setEditingSeason({
                      ...editingSeason,
                      showDivisions: e.target.checked,
                    })
                  }
                />
                Tournament
              </label>

              <button type="submit">Update</button>
              <button
                type="button"
                onClick={() => setIsEditSeasonModalOpen(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationDetails;
